import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { CalendarIcon, Clock, Phone, Mail, Building, MapPin, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { LocationAutocomplete } from "@/components/location-autocomplete";
import { useNavigate } from "react-router-dom";
import { validateAndSanitizeFormData, appointmentRateLimiter } from "@/lib/security";

export function AppointmentBooking() {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    companyType: "",
    planType: "",
    location: "",
    message: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Leer parámetro de plan de la URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const planParam = urlParams.get('plan');
    if (planParam) {
      setFormData(prev => ({
        ...prev,
        planType: planParam
      }));
    }
  }, []);

  const timeSlots = [
    "09:00", "10:00", "11:00", "12:00", 
    "14:00", "15:00", "16:00", "17:00"
  ];

  const companyTypes = [
    "Instaladores de placas solares",
    "Reformas del hogar"
  ];

  const planTypes = [
    "Starter",
    "Growth",
    "Premium Guardian",
    "No estoy seguro - Necesito asesoría"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate and sanitize form data
    const { isValid, errors, sanitizedData } = validateAndSanitizeFormData(formData);
    
    if (!selectedDate || !selectedTime) {
      toast({
        title: "Campos requeridos",
        description: "Por favor selecciona fecha y hora",
        variant: "destructive"
      });
      return;
    }

    if (!isValid) {
      toast({
        title: "Datos inválidos",
        description: errors.join(", "),
        variant: "destructive"
      });
      return;
    }

    // Rate limiting check
    const userIdentifier = `${sanitizedData.email}_${sanitizedData.phone}`;
    if (!appointmentRateLimiter.isAllowed(userIdentifier)) {
      toast({
        title: "Demasiadas solicitudes",
        description: "Por favor espera unos minutos antes de enviar otra solicitud",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Ensure we have an anonymous session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('Creating anonymous session...');
        const { data: anonData, error: anonError } = await supabase.auth.signInAnonymously();
        if (anonError) {
          console.error('Error creating anonymous session:', anonError);
          throw anonError;
        }
        console.log('Anonymous session created:', anonData);
      } else {
        console.log('Existing session found:', session);
      }
      
      // Save lead to Supabase with sanitized data
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .insert([{
          name: sanitizedData.name,
          email: sanitizedData.email,
          phone: sanitizedData.phone,
          company: sanitizedData.company || null,
          source: 'appointment_booking',
          status: 'new',
          notes: `${sanitizedData.companyType ? `Tipo de empresa: ${sanitizedData.companyType}. ` : ''}${sanitizedData.planType ? `Plan de interés: ${sanitizedData.planType}. ` : ''}${sanitizedData.location ? `Ubicación: ${sanitizedData.location}. ` : ''}${sanitizedData.message || ''}`
        }])
        .select()
        .single();

      if (leadError) {
        console.error('Error saving lead:', leadError);
        throw leadError;
      }

      // Save appointment
      const { error: appointmentError } = await supabase
        .from('appointments')
        .insert([{
          lead_id: leadData.id,
          date: selectedDate.toISOString().split('T')[0],
          time: selectedTime,
          status: 'pending',
          notes: `${formData.companyType ? `Tipo de empresa: ${formData.companyType}` : 'Consultoría general'}${formData.planType ? ` - Plan: ${formData.planType}` : ''}`
        }]);

      if (appointmentError) {
        console.error('Error saving appointment:', appointmentError);
        throw appointmentError;
      }

      // Send email notification
      try {
        await supabase.functions.invoke('send-lead-email', {
          body: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            companyType: formData.companyType,
            planType: formData.planType,
            location: formData.location,
            message: formData.message,
            date: format(selectedDate, "dd/MM/yyyy"),
            time: selectedTime
          }
        });
        console.log('Email notification sent successfully');
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
        // Don't throw here - the lead was saved successfully
      }


      toast({
        title: "¡Cita agendada exitosamente!",
        description: `Te contactaremos pronto para confirmar tu cita del ${format(selectedDate, "dd/MM/yyyy")} a las ${selectedTime}`,
      });

      // Limpiar formulario
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        companyType: "",
        planType: "",
        location: "",
        message: ""
      });
      setSelectedDate(undefined);
      setSelectedTime("");

    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error al agendar",
        description: "Hubo un problema al procesar tu solicitud. Inténtalo de nuevo.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Card className="max-w-2xl mx-auto bg-card/80 backdrop-blur border-primary/20">
      <CardHeader className="text-center relative">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/')}
          className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </Button>
        <CardTitle className="text-xl sm:text-2xl gradient-gold bg-clip-text text-transparent">
          Agenda tu Consultoría de Marketing Digital
        </CardTitle>
        <p className="text-sm sm:text-base text-muted-foreground">
          Completamente gratuita y sin compromiso. Analizaremos cómo mejorar tu visibilidad online y conseguir más clientes.
        </p>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Información Personal */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Nombre completo *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Tu nombre"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="tu@email.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Teléfono *
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+34 600 000 000"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="company" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                Empresa
              </Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                placeholder="Nombre de tu empresa"
              />
            </div>
          </div>

          {/* Servicio y Ubicación */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de empresa</Label>
              <Select value={formData.companyType} onValueChange={(value) => setFormData({...formData, companyType: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el tipo de empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companyTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Plan de interés</Label>
              <Select value={formData.planType} onValueChange={(value) => setFormData({...formData, planType: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el plan que te interesa" />
                </SelectTrigger>
                <SelectContent>
                  {planTypes.map((plan) => (
                    <SelectItem key={plan} value={plan}>
                      {plan}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selección de Fecha y Hora */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                Fecha preferida *
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "dd/MM/yyyy") : "Selecciona una fecha"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Hora preferida *
              </Label>
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una hora" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Ubicación */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Ubicación de tu empresa
            </Label>
            <LocationAutocomplete
              value={formData.location}
              onChange={(value) => setFormData({...formData, location: value})}
              placeholder="Selecciona o escribe tu ciudad"
            />
          </div>

          {/* Mensaje */}
          <div className="space-y-2">
            <Label htmlFor="message">Cuéntanos sobre tu empresa y objetivos</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Describe tu empresa, situación actual, objetivos y qué esperas conseguir..."
              rows={3}
            />
          </div>


          <Button 
            type="submit" 
            className="w-full gradient-gold hover-lift text-base sm:text-lg py-4 sm:py-6"
            disabled={isLoading}
          >
            {isLoading ? "Agendando..." : "Confirmar Cita"}
          </Button>
          
          <p className="text-xs text-muted-foreground text-center px-4">
            * Campos obligatorios. Al enviar este formulario aceptas ser contactado por MKTN GUARDIANS.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}